import { useEffect, useState } from 'react'
import { Button } from '@agms/ui'
import { usePricingTemplates } from '../../hooks/useReferenceData'
import { formatCurrencyWithCentsOrDash } from '../../lib/format'
import {
  categoryTotalWarning,
  FEE_BUCKET_WARNING,
  FEE_CHECK_MESSAGE,
  feesNeedChecking,
  openingSliders,
  previewSavings,
  type SavingsSliders,
  type StatementSnapshot,
} from '../../lib/statementSavings'

interface FeeLine {
  description: string | null
  sales_amount: string | null
  item_count: number | null
  discount_rate: string | null
  per_item_rate: string | null
  fee_amount: string | null
  confidence: number | null
  page: number | null
}

interface FeeCategory {
  name: string
  amount: string | null
  confidence: number | null
  page: number | null
  lines: FeeLine[]
}

interface ExtractionField {
  value?: string | number | null
  confidence?: number | null
  page?: number | null
}

export interface StatementDashboardAnalysis extends StatementSnapshot {
  id: string
  processor_name?: string | null
  statement_period?: string | null
  reconciliation_warnings?: string[]
  result?: { interchange_cost?: string | null; uses_unverified_rates?: boolean } | null
  extraction?: {
    category_sum_warning?: string | null
    truncated?: boolean
    confidence_threshold?: string | number | null
    fields?: Record<string, ExtractionField>
    fee_categories?: FeeCategory[]
    fee_lines?: Array<{ bucket: string; amount: string | null; confidence: number | null; label: string | null; page?: number | null }>
  } | null
  visa_discount_percent?: string | null
  mastercard_discount_percent?: string | null
  discover_discount_percent?: string | null
  amex_discount_percent?: string | null
  pin_debit_discount_percent?: string | null
  visa_per_item_fee?: string | null
  mastercard_per_item_fee?: string | null
  discover_per_item_fee?: string | null
  amex_per_item_fee?: string | null
  pin_debit_per_item_fee?: string | null
}

interface StatementDashboardProps {
  analysis: StatementDashboardAnalysis
  reviewPending: boolean
  lowConfidenceFields: string[]
  onConfirm: () => void
  onAnalyze: () => void
  confirmPending: boolean
  analyzePending: boolean
  onEditFigures: () => void
}

const CARD_MIX = [
  { key: 'visa', label: 'Visa', volume: 'visa_volume', count: 'visa_count', rate: 'visa_discount_percent', perItem: 'visa_per_item_fee' },
  { key: 'mastercard', label: 'Mastercard', volume: 'mastercard_volume', count: 'mastercard_count', rate: 'mastercard_discount_percent', perItem: 'mastercard_per_item_fee' },
  { key: 'discover', label: 'Discover', volume: 'discover_volume', count: 'discover_count', rate: 'discover_discount_percent', perItem: 'discover_per_item_fee' },
  { key: 'amex', label: 'Amex', volume: 'amex_volume', count: 'amex_count', rate: 'amex_discount_percent', perItem: 'amex_per_item_fee' },
  { key: 'pin_debit', label: 'PIN debit', volume: 'pin_debit_volume', count: 'pin_debit_count', rate: 'pin_debit_discount_percent', perItem: 'pin_debit_per_item_fee' },
] as const

const BAR_COLORS = ['#0f766e', '#0369a1', '#b45309', '#6d28d9', '#be123c', '#334155']

const FIGURE_LABELS: Record<string, string> = {
  total_fees: 'Total fees',
  gross_volume: 'Sales',
  effective_rate: 'Effective rate',
  visa_volume: 'Visa',
  mastercard_volume: 'Mastercard',
  discover_volume: 'Discover',
  amex_volume: 'Amex',
  pin_debit_volume: 'PIN debit',
}

function percentText(value: number | null): string {
  if (value === null) return '—'
  return `${value.toFixed(2)}%`
}

function sourceSentence(
  label: string,
  field: ExtractionField | undefined,
  low: boolean,
): string {
  if (!field) return `${label} was entered on this analysis. No statement page is attached.`
  if (typeof field.page !== 'number') {
    return `${label}. No source page was read, so this figure is not confirmed.`
  }
  const confidence = typeof field.confidence === 'number'
    ? `The reading is ${Math.round(field.confidence * 100)}% sure.`
    : 'No confidence was read.'
  const check = low ? ' Check this number before you confirm.' : ''
  return `${label}. Source page ${field.page}. ${confidence}${check}`
}

export function StatementDashboard({
  analysis,
  reviewPending,
  lowConfidenceFields,
  onConfirm,
  onAnalyze,
  confirmPending,
  analyzePending,
  onEditFigures,
}: StatementDashboardProps) {
  const templates = usePricingTemplates()
  const [sliders, setSliders] = useState<SavingsSliders | null>(null)
  const [selectedFigure, setSelectedFigure] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState(0)
  const opening = openingSliders(analysis)
  const preview = previewSavings(
    analysis,
    sliders,
    templates.data ?? [],
    {
      hasResult: analysis.result != null,
      amount: analysis.result?.interchange_cost,
    },
  )
  const categories = analysis.extraction?.fee_categories ?? []
  const categoryWarning = categoryTotalWarning(
    categories,
    analysis.total_fees,
    analysis.extraction?.category_sum_warning,
  )
  let printedCost: number | null = null
  for (const card of CARD_MIX) {
    const cost = printedCostFor(analysis[card.volume], analysis[card.count], analysis[card.rate], analysis[card.perItem])
    if (cost === null) continue
    printedCost = (printedCost ?? 0) + cost
  }
  const checkFees = feesNeedChecking({
    fees: preview.fees,
    sales: preview.sales,
    printedCost,
    categoriesDisagree: categoryWarning !== null,
    bucketsDisagree: (analysis.reconciliation_warnings ?? []).includes(FEE_BUCKET_WARNING),
  })
  const categoryTotal = Number(analysis.total_fees ?? preview.fees ?? 0)
  const low = new Set(lowConfidenceFields)
  const hasFigures = (preview.sales ?? 0) > 0 || (preview.fees ?? 0) > 0 || categories.length > 0
  const activeSliders = sliders ?? opening
  const fields = analysis.extraction?.fields ?? {}

  useEffect(() => {
    setSliders(null)
    setSelectedFigure(null)
    setSelectedCategory(0)
  }, [analysis.id, analysis.gross_volume, analysis.total_fees, analysis.pricing_model])

  const toggleFigure = (key: string) => {
    setSelectedFigure((current) => (current === key ? null : key))
  }

  return (
    <section className="space-y-6" aria-label="Statement summary">
      {reviewPending && analysis.extraction == null ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
          The statement file changed. Read it again, or confirm the figures before running the analysis.
        </p>
      ) : null}
      {reviewPending && analysis.extraction != null ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
          These figures were read from the statement. Check the highlighted numbers, then confirm.
        </p>
      ) : null}
      {reviewPending && analysis.extraction?.truncated ? (
        <p className="text-sm text-amber-800" role="status">Only part of the statement was read. Check every figure.</p>
      ) : null}
      {categoryWarning ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
          {categoryWarning}
        </p>
      ) : null}
      {analysis.reconciliation_warnings && analysis.reconciliation_warnings.length > 0 ? (
        <ul className="list-disc rounded-md border border-amber-300 bg-amber-50 p-3 pl-8 text-sm text-amber-950" role="alert">
          {analysis.reconciliation_warnings.map((warning) => <li key={warning}>{warning}</li>)}
        </ul>
      ) : null}

      {!hasFigures ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">No statement figures yet</h2>
          <p className="mt-2 text-sm text-gray-600">Add the sales and fees from the statement.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-gray-500">
              {[analysis.statement_period, analysis.processor_name].filter(Boolean).join(' · ') || 'Statement'}
            </p>
            {checkFees ? (
              <>
                <h2 className="mt-3 text-sm font-medium text-gray-600">Fees need checking</h2>
                <p className="mt-3 text-lg font-semibold text-gray-900">{FEE_CHECK_MESSAGE}</p>
                <p className="mt-2 text-sm text-gray-600">Fix the total fees. The rate and the savings comparison stay off until they can be trusted.</p>
              </>
            ) : (
              <>
                <h2 className="mt-3 text-sm font-medium text-gray-600">Effective rate</h2>
                <button
                  type="button"
                  className={figureClass(low.has('total_fees') || low.has('gross_volume'))}
                  aria-expanded={selectedFigure === 'effective_rate'}
                  title="Fees divided by sales."
                  onClick={() => toggleFigure('effective_rate')}
                >
                  <span className="text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">{percentText(preview.effectiveRate)}</span>
                  <span className="mt-1 block text-sm text-gray-600">of sales went to fees</span>
                </button>
                <p className="mt-2 text-sm text-gray-500">Fees divided by sales. Select a number to see where it was read.</p>
              </>
            )}
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <FigureButton
                figureKey="total_fees"
                label="Total fees"
                value={formatCurrencyWithCentsOrDash(preview.fees)}
                low={low.has('total_fees')}
                selected={selectedFigure === 'total_fees'}
                onSelect={toggleFigure}
              />
              <FigureButton
                figureKey="gross_volume"
                label="Sales"
                value={formatCurrencyWithCentsOrDash(preview.sales)}
                low={low.has('gross_volume')}
                selected={selectedFigure === 'gross_volume'}
                onSelect={toggleFigure}
              />
            </div>
            {!checkFees && preview.effectiveRate === null ? (
              <p className="mt-3 text-sm text-gray-600">The rate needs sales greater than zero.</p>
            ) : null}
            {selectedFigure ? (
              <p className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-800" role="status">
                {sourceSentence(
                  FIGURE_LABELS[selectedFigure] ?? 'Figure',
                  selectedFigure === 'effective_rate' ? fields.total_fees : fields[selectedFigure],
                  selectedFigure === 'effective_rate'
                    ? low.has('total_fees') || low.has('gross_volume')
                    : low.has(selectedFigure),
                )}
                {selectedFigure === 'effective_rate' && preview.effectiveRate !== null
                  ? ` Full rate ${preview.effectiveRate.toFixed(4)}%.`
                  : ''}
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Where the money went</h2>
            <p className="mt-1 text-sm text-gray-600">The fee sections printed on the statement. Choose one to see its lines.</p>
            {categories.length === 0 ? (
              <p className="mt-4 text-sm text-gray-600">This statement has no fee sections yet.</p>
            ) : (
              <>
                <div className="mt-4 flex h-12 overflow-hidden rounded-xl" aria-hidden="true">
                  {categories.map((category, index) => {
                    const amount = Number(category.amount ?? 0)
                    const width = categoryTotal > 0 ? Math.max(2, (amount / categoryTotal) * 100) : 100 / categories.length
                    return (
                      <div
                        key={`${category.name}-bar-${index}`}
                        style={{ width: `${width}%`, backgroundColor: BAR_COLORS[index % BAR_COLORS.length] }}
                      />
                    )
                  })}
                </div>
                <ul className="mt-4 space-y-2">
                  {categories.map((category, index) => {
                    const categoryLow = reviewPending && (category.confidence ?? 1) < Number(analysis.extraction?.confidence_threshold ?? 0.8)
                    return (
                      <li key={`${category.name}-${index}`}>
                        <button
                          type="button"
                          className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm ${selectedCategory === index ? 'bg-sky-50 ring-2 ring-sky-600' : 'hover:bg-gray-50'} ${categoryLow ? 'bg-amber-50 ring-2 ring-amber-400' : ''}`}
                          aria-pressed={selectedCategory === index}
                          onClick={() => setSelectedCategory(index)}
                        >
                          <span className="flex items-center gap-2 font-medium text-gray-900">
                            <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: BAR_COLORS[index % BAR_COLORS.length] }} aria-hidden="true" />
                            {category.name}
                            {categoryLow ? <span className="text-amber-800">Check this</span> : null}
                          </span>
                          <span className="text-gray-700">{formatCurrencyWithCentsOrDash(category.amount)}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
                <CategoryLines category={categories[selectedCategory] ?? null} reviewPending={reviewPending} threshold={Number(analysis.extraction?.confidence_threshold ?? 0.8)} />
              </>
            )}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Card mix</h2>
            <p className="mt-1 text-sm text-gray-600">Volume, the rate printed on the statement, and what that rate cost.</p>
            <ul className="mt-4 space-y-3">
              {CARD_MIX.map((card) => {
                const volume = analysis[card.volume]
                const count = analysis[card.count]
                const rate = analysis[card.rate]
                const perItem = analysis[card.perItem]
                const volumeNumber = volume === null || volume === undefined || volume === '' ? null : Number(volume)
                const width = preview.sales && volumeNumber ? Math.min(100, (volumeNumber / preview.sales) * 100) : 0
                const cost = printedCostFor(volume, count, rate, perItem)
                const cardLow = low.has(card.volume) || low.has(card.rate)
                return (
                  <li key={card.key}>
                    <button
                      type="button"
                      className={`w-full rounded-lg px-2 py-2 text-left ${figureClass(cardLow)}`}
                      aria-expanded={selectedFigure === card.volume}
                      onClick={() => toggleFigure(card.volume)}
                    >
                      <span className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="font-medium text-gray-900">{card.label}</span>
                        <span className="text-gray-700">
                          {formatCurrencyWithCentsOrDash(volumeNumber)}
                          {' · '}
                          {rate === null || rate === undefined || rate === '' ? 'Rate not on the statement' : `${rate}%`}
                          {' · '}
                          {cost === null ? '—' : formatCurrencyWithCentsOrDash(cost)}
                        </span>
                      </span>
                      <span className="mt-2 block h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                        <span className="block h-2 rounded-full bg-sky-700" style={{ width: `${width}%` }} />
                      </span>
                      {cardLow ? <span className="mt-1 block text-xs text-amber-800">Check this</span> : null}
                    </button>
                  </li>
                )
              })}
            </ul>
            {selectedFigure && CARD_MIX.some((card) => card.volume === selectedFigure) ? (
              <p className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-800" role="status">
                {sourceSentence(FIGURE_LABELS[selectedFigure] ?? 'Card', fields[selectedFigure], low.has(selectedFigure))}
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">What they would pay with AGMS</h2>
            <p className="mt-1 text-sm text-gray-600">
              The same sales, priced with the rate saved on this analysis or a pricing template. This preview stays on this page.
            </p>
            {reviewPending ? (
              <p className="mt-4 text-sm text-gray-700">Confirm the figures first. Then you can compare prices.</p>
            ) : checkFees ? (
              <p className="mt-4 text-sm text-gray-700">{FEE_CHECK_MESSAGE}</p>
            ) : (
              <>
                {preview.contextNote ? <p className="mt-4 text-sm text-gray-700">{preview.contextNote}</p> : null}
                {analysis.result?.uses_unverified_rates ? (
                  <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                    An interchange rate used for the saved comparison is unverified. Check it before you rely on the savings.
                  </p>
                ) : null}
                <div className="mt-4 grid gap-4 md:grid-cols-2" aria-live="polite">
                  <OfferCard title="Interchange plus" offer={preview.interchangePlus} note={preview.interchangePlusNote} today={preview.comparisonFees} />
                  <OfferCard title="Flat rate" offer={preview.flat} note={preview.flatNote} today={preview.comparisonFees} />
                </div>
                {preview.templateFootnote ? <p className="mt-3 text-xs text-gray-500">{preview.templateFootnote}</p> : null}
                {activeSliders ? (
                  <div className="mt-6 space-y-4">
                    <Slider
                      label="Monthly sales"
                      hint="Changes both prices. The monthly fee stays the same."
                      min={opening ? opening.volume * 0.5 : 0}
                      max={opening ? opening.volume * 2 : 0}
                      step={opening && opening.volume >= 10000 ? 100 : 10}
                      value={activeSliders.volume}
                      display={formatCurrencyWithCentsOrDash(activeSliders.volume)}
                      onChange={(volume) => setSliders({ ...(sliders ?? activeSliders), volume })}
                    />
                    <Slider
                      label="Average sale"
                      hint="Changes how many sales the per-sale fee uses."
                      min={opening && opening.averageTicket > 0 ? Math.max(1, opening.averageTicket * 0.5) : 1}
                      max={opening && opening.averageTicket > 0 ? opening.averageTicket * 2 : 2}
                      step={opening && opening.averageTicket >= 20 ? 1 : 0.5}
                      value={activeSliders.averageTicket}
                      display={formatCurrencyWithCentsOrDash(activeSliders.averageTicket)}
                      onChange={(averageTicket) => setSliders({ ...(sliders ?? activeSliders), averageTicket })}
                    />
                    <Slider
                      label="PIN debit share"
                      hint="Share of sales that run as PIN debit. Card rates still come from the saved price or the template."
                      min={0}
                      max={100}
                      step={1}
                      value={activeSliders.pinDebitShare}
                      display={`${Math.round(activeSliders.pinDebitShare)}%`}
                      onChange={(pinDebitShare) => setSliders({ ...(sliders ?? activeSliders), pinDebitShare })}
                    />
                    <Button type="button" variant="outline" onClick={() => setSliders(null)}>Use the statement amounts</Button>
                  </div>
                ) : null}
              </>
            )}
          </div>

        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {reviewPending ? (
          <Button type="button" size="lg" onClick={onConfirm} disabled={confirmPending}>
            Confirm these figures
          </Button>
        ) : null}
        <Button type="button" size="lg" onClick={onAnalyze} disabled={analyzePending || reviewPending}>
          Run analysis
        </Button>
        <Button type="button" variant="outline" onClick={onEditFigures}>Edit figures</Button>
      </div>
    </section>
  )
}

function figureClass(low: boolean): string {
  return low
    ? 'mt-1 block w-full rounded-lg bg-amber-50 p-2 text-left ring-2 ring-amber-400'
    : 'mt-1 block w-full rounded-lg p-2 text-left hover:bg-gray-50'
}

function FigureButton({
  figureKey,
  label,
  value,
  low,
  selected,
  onSelect,
}: {
  figureKey: string
  label: string
  value: string
  low: boolean
  selected: boolean
  onSelect: (key: string) => void
}) {
  return (
    <button
      type="button"
      className={figureClass(low)}
      aria-expanded={selected}
      onClick={() => onSelect(figureKey)}
    >
      <span className="block text-sm text-gray-500">{label}</span>
      <span className="block text-2xl font-semibold text-gray-900">{value}</span>
      {low ? <span className="mt-1 block text-xs text-amber-800">Check this</span> : null}
    </button>
  )
}

function CategoryLines({
  category,
  reviewPending,
  threshold,
}: {
  category: FeeCategory | null
  reviewPending: boolean
  threshold: number
}) {
  if (!category) return null
  return (
    <div className="mt-4 rounded-lg border border-gray-200 p-3">
      <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
      {typeof category.page === 'number' ? <p className="mt-1 text-xs text-gray-600">Source page {category.page}.</p> : null}
      {category.lines.length === 0 ? (
        <p className="mt-2 text-sm text-gray-600">No line items on this section.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {category.lines.map((line, index) => {
            const lineLow = reviewPending && (line.confidence ?? 1) < threshold
            return (
              <li key={`${category.name}-${index}`} className={lineLow ? 'text-sm text-amber-950' : 'text-sm text-gray-700'}>
                {line.description || 'Fee'}
                {line.sales_amount ? `, sales ${formatCurrencyWithCentsOrDash(line.sales_amount)}` : ''}
                {line.item_count != null ? `, count ${line.item_count}` : ''}
                {line.discount_rate ? `, discount ${line.discount_rate}` : ''}
                {line.per_item_rate ? `, per item ${line.per_item_rate}` : ''}
                {line.fee_amount ? `, fee ${formatCurrencyWithCentsOrDash(line.fee_amount)}` : ''}
                {typeof line.page === 'number' ? `, source page ${line.page}` : ''}
                {lineLow ? ' — low confidence' : ''}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function OfferCard({
  title,
  offer,
  note,
  today,
}: {
  title: string
  offer: { cost: number; savings: number | null; summary: string; source: string } | null
  note: string | null
  today: number | null
}) {
  const savings = offer?.savings
  const headline = savings === null || savings === undefined
    ? null
    : savings > 0
      ? `They save ${formatCurrencyWithCentsOrDash(savings)}`
      : savings < 0
        ? `Costs ${formatCurrencyWithCentsOrDash(Math.abs(savings))} more`
        : 'Same as today'
  return (
    <article className="rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {offer ? (
        <>
          <p className="mt-3 text-3xl font-semibold text-gray-900">{headline ?? formatCurrencyWithCentsOrDash(offer.cost)}</p>
          <p className="mt-2 text-sm text-gray-600">Today {formatCurrencyWithCentsOrDash(today)} · with AGMS {formatCurrencyWithCentsOrDash(offer.cost)}</p>
          <p className="mt-2 text-sm text-gray-700">{offer.summary}</p>
          <p className="mt-1 text-xs text-gray-500">{offer.source}</p>
        </>
      ) : (
        <p className="mt-3 text-sm text-gray-700">{note}</p>
      )}
    </article>
  )
}

function Slider({
  label,
  hint,
  min,
  max,
  step,
  value,
  display,
  onChange,
}: {
  label: string
  hint: string
  min: number
  max: number
  step: number
  value: number
  display: string
  onChange: (value: number) => void
}) {
  const id = label.toLowerCase().replace(/\s+/g, '-')
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-gray-800" title={hint}>{label}</label>
        <span className="text-sm text-gray-700">{display}</span>
      </div>
      <input
        id={id}
        className="mt-2 h-11 w-full accent-sky-700"
        type="range"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(value) ? value : min}
        aria-valuetext={display}
        title={hint}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500">{hint}</p>
    </div>
  )
}

function printedCostFor(
  volume: string | number | null | undefined,
  count: string | number | null | undefined,
  rate: string | number | null | undefined,
  perItem: string | number | null | undefined,
): number | null {
  if ((rate === null || rate === undefined || rate === '') && (perItem === null || perItem === undefined || perItem === '')) {
    return null
  }
  const volumeNumber = Number(volume ?? 0)
  const countNumber = Number(count ?? 0)
  const rateNumber = rate === null || rate === undefined || rate === '' ? 0 : Number(rate)
  const perItemNumber = perItem === null || perItem === undefined || perItem === '' ? 0 : Number(perItem)
  if (![volumeNumber, countNumber, rateNumber, perItemNumber].every(Number.isFinite)) return null
  return Math.round((volumeNumber * rateNumber / 100 + countNumber * perItemNumber) * 100) / 100
}
